import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable, defer, of } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';

import { AppConfig } from '../../../../../app.config';
import { HelpersService } from '../../../../../services/helpers.service';
import { ReceiptTemplate, TemplateType } from '../models/receipt-template.model';

// ─────────────────────────────────────────────────────────────────────────────
// API CONTRACT
// ─────────────────────────────────────────────────────────────────────────────
//
// Base URL:  {env.url}/Portal/Workshop/ReceiptTemplates
// Auth:      Bearer token in Authorization header  (via getNewApiHeaders)
// Scoping:   PartnerID header  (via getNewApiHeaders)
// Code:      ?code={env.key}  appended to every URL
//
// ── 1. List templates ────────────────────────────────────────────────────────
// GET /Portal/Workshop/ReceiptTemplates?code={key}
//
// Response 200 — ReceiptTemplate[]
// [
//   {
//     "id": "abc123",
//     "name": "Default Job Card",
//     "type": "JOB_CARD",            // "JOB_CARD" | "THIRD_PARTY_DISPATCH"
//     "isGlobalDefault": true,
//     "isActive": true,
//     "widthMm": 210,
//     "heightMm": 297,
//     "orientation": "portrait",     // "portrait" | "landscape"
//     "elements": [ ...see element shape below... ],
//     "createdAt": "2026-04-09T10:00:00Z",
//     "updatedAt": "2026-04-09T11:00:00Z"
//   }
// ]
//
// ── 2. Get single template ───────────────────────────────────────────────────
// GET /Portal/Workshop/ReceiptTemplates/{id}?code={key}
//
// Response 200 — ReceiptTemplate (same shape as above, single object)
// Response 404 — template not found
//
// ── 3. Create template ───────────────────────────────────────────────────────
// POST /Portal/Workshop/ReceiptTemplates?code={key}
//
// Request body — ReceiptTemplate (omit id / createdAt / updatedAt; server assigns them)
// {
//   "name": "My Template",
//   "type": "JOB_CARD",
//   "isGlobalDefault": false,
//   "isActive": true,
//   "widthMm": 210,
//   "heightMm": 297,
//   "orientation": "portrait",
//   "elements": [ ...element objects... ]
// }
//
// Response 201 — ReceiptTemplate (full object with server-assigned id and timestamps)
//
// ── 4. Update template ───────────────────────────────────────────────────────
// PUT /Portal/Workshop/ReceiptTemplates/{id}?code={key}
//
// Request body — full ReceiptTemplate (including id)
// If isActive is true  → server deactivates all other templates of the same type
// If isGlobalDefault is true → server clears isGlobalDefault on all other same-type templates
//
// Response 200 — ReceiptTemplate (updated object with new updatedAt)
//
// ── 5. Delete template ───────────────────────────────────────────────────────
// DELETE /Portal/Workshop/ReceiptTemplates/{id}?code={key}
//
// Response 204 — No Content
//
// ── 6. Duplicate template ────────────────────────────────────────────────────
// POST /Portal/Workshop/ReceiptTemplates/{id}/duplicate?code={key}
//
// No request body required.
// Server creates a copy with:
//   name:            "{original name} (Copy)"
//   isGlobalDefault: false
//   isActive:        false   ← copy starts as a draft
//   id:              new server-assigned id
//   createdAt/updatedAt: now
//
// Response 201 — ReceiptTemplate (the new copy)
//
// ── 7. Set active (mutual exclusivity) ──────────────────────────────────────
// PATCH /Portal/Workshop/ReceiptTemplates/{id}/active?code={key}
//
// Request body:  { "type": "JOB_CARD" }
// Server sets isActive=true on the given template and isActive=false on ALL
// other templates of the same type for this partner.
// Response 200 — ReceiptTemplate[]  (all templates of that type, with updated isActive)
//
// ── 8. Set global default (mutual exclusivity) ───────────────────────────────
// PATCH /Portal/Workshop/ReceiptTemplates/{id}/default?code={key}
//
// Request body:  { "type": "JOB_CARD" }
// Server sets isGlobalDefault=true on the given template and false on all others.
// Response 200 — ReceiptTemplate[]  (all templates of that type, with updated isGlobalDefault)
//
// ── Element shape (inside ReceiptTemplate.elements) ──────────────────────────
// {
//   "id": "el_abc123",
//   "tagKey": "customer_name",
//   "label": "Customer Name",
//   "elementType": "field",
//   "content": null,
//   "x": 10.0, "y": 20.0, "width": 60.0, "height": 8.0,
//   "showLabel": false,
//   "fontSize": 10,
//   "fontWeight": "normal",
//   "fontStyle": "normal",
//   "textAlign": "left",
//   "color": "#000000",
//   "backgroundColor": "transparent",
//   "zIndex": 1,
//   "columnWidths": [40, 40, 20],
//   "columnAlignments": ["left", "left", "right"]
// }
// ─────────────────────────────────────────────────────────────────────────────

@Injectable({ providedIn: 'root' })
export class ReceiptTemplateService {

  /** Local reactive cache — populated on refresh() and kept in sync with mutations. */
  private readonly cache$$ = new BehaviorSubject<ReceiptTemplate[]>([]);

  /** Observable of the full template list. Subscribe for live updates. */
  get templates$(): Observable<ReceiptTemplate[]> {
    return this.cache$$.asObservable();
  }

  constructor(
    private http: HttpClient,
    private helpersService: HelpersService,
    private appConfig: AppConfig,
  ) {}

  // ─── URL / header helpers ─────────────────────────────────────────────────

  private get env()     { return this.appConfig.getEnvironmentNew(); }
  private get baseUrl() { return `${this.env.url}/Portal/Workshop/ReceiptTemplates`; }
  private get listUrl() { return `${this.baseUrl}?code=${this.env.key}`; }
  private itemUrl(path: string) { return `${this.baseUrl}/${path}?code=${this.env.key}`; }
  private get reqHeaders() { return this.helpersService.getNewApiHeaders(); }

  // ─── Cache helpers ────────────────────────────────────────────────────────

  private upsertCache(template: ReceiptTemplate): void {
    const current = this.cache$$.getValue();
    const idx = current.findIndex(t => t.id === template.id);
    this.cache$$.next(
      idx >= 0 ? current.map(t => t.id === template.id ? template : t)
               : [...current, template],
    );
  }

  private removeFromCache(id: string): void {
    this.cache$$.next(this.cache$$.getValue().filter(t => t.id !== id));
  }

  private mergeTypeIntoCache(incoming: ReceiptTemplate[], type: TemplateType): void {
    const rest = this.cache$$.getValue().filter(t => t.type !== type);
    this.cache$$.next([...rest, ...incoming]);
  }

  // ─── Public API ───────────────────────────────────────────────────────────

  /**
   * Returns the reactive cache observable.
   * Subscribe to this for live UI updates — it emits whenever the cache changes.
   */
  getTemplates(): Observable<ReceiptTemplate[]> {
    return this.templates$;
  }

  /**
   * Fetches all templates from the API and updates the cache.
   * Call this once on component init (and again after navigation back to the list).
   * Returns an Observable that completes when the HTTP call finishes — subscribe
   * to know when loading is done.
   */
  refresh(): Observable<ReceiptTemplate[]> {
    return defer(() =>
      this.http.get<ReceiptTemplate[]>(this.listUrl, this.reqHeaders),
    ).pipe(
      tap(templates => this.cache$$.next(templates)),
      catchError(err => {
        console.error('[ReceiptTemplateService] refresh failed:', err);
        return of(this.cache$$.getValue());
      }),
    );
  }

  /**
   * Fetches a single template by ID.
   * Updates the cache entry on success.
   */
  getTemplateById(id: string): Observable<ReceiptTemplate | undefined> {
    return defer(() =>
      this.http.get<ReceiptTemplate>(this.itemUrl(id), this.reqHeaders),
    ).pipe(
      tap(template => this.upsertCache(template)),
      catchError(err => {
        console.error('[ReceiptTemplateService] getTemplateById failed:', err);
        return of(undefined);
      }),
    );
  }

  /**
   * Creates (POST) or updates (PUT) a template.
   * When isActive / isGlobalDefault is true the server enforces mutual
   * exclusivity across all templates of the same type.
   */
  saveTemplate(template: ReceiptTemplate): Observable<ReceiptTemplate> {
    const isNew = !template.id;
    return defer(() =>
      isNew
        ? this.http.post<ReceiptTemplate>(this.listUrl, template, this.reqHeaders)
        : this.http.put<ReceiptTemplate>(this.itemUrl(template.id), template, this.reqHeaders),
    ).pipe(
      tap(saved => this.upsertCache(saved)),
      catchError(err => {
        console.error('[ReceiptTemplateService] saveTemplate failed:', err);
        return of(template);
      }),
    );
  }

  deleteTemplate(id: string): Observable<void> {
    return defer(() =>
      this.http.delete<void>(this.itemUrl(id), this.reqHeaders),
    ).pipe(
      tap(() => this.removeFromCache(id)),
      map(() => undefined),
      catchError(err => {
        console.error('[ReceiptTemplateService] deleteTemplate failed:', err);
        return of(undefined);
      }),
    );
  }

  /** Server creates a copy; the copy starts as a draft (isActive: false). */
  duplicateTemplate(id: string): Observable<ReceiptTemplate | undefined> {
    return defer(() =>
      this.http.post<ReceiptTemplate>(this.itemUrl(`${id}/duplicate`), {}, this.reqHeaders),
    ).pipe(
      tap(copy => this.cache$$.next([...this.cache$$.getValue(), copy])),
      catchError(err => {
        console.error('[ReceiptTemplateService] duplicateTemplate failed:', err);
        return of(undefined);
      }),
    );
  }

  // ─── Mutual-exclusivity operations ────────────────────────────────────────

  /** Activates the given template and deactivates all other same-type templates. */
  setActive(id: string, type: TemplateType): Observable<void> {
    return defer(() =>
      this.http.patch<ReceiptTemplate[]>(this.itemUrl(`${id}/active`), { type }, this.reqHeaders),
    ).pipe(
      tap(updated => this.mergeTypeIntoCache(updated, type)),
      map(() => undefined),
      catchError(err => {
        console.error('[ReceiptTemplateService] setActive failed:', err);
        return of(undefined);
      }),
    );
  }

  /** Sets the given template as the global default; clears all other same-type defaults. */
  setGlobalDefault(id: string, type: TemplateType): Observable<void> {
    return defer(() =>
      this.http.patch<ReceiptTemplate[]>(this.itemUrl(`${id}/default`), { type }, this.reqHeaders),
    ).pipe(
      tap(updated => this.mergeTypeIntoCache(updated, type)),
      map(() => undefined),
      catchError(err => {
        console.error('[ReceiptTemplateService] setGlobalDefault failed:', err);
        return of(undefined);
      }),
    );
  }

  getGlobalDefault(type: TemplateType): ReceiptTemplate | undefined {
    return this.cache$$.getValue().find(t => t.type === type && t.isGlobalDefault);
  }

  // ─── Derived queries ──────────────────────────────────────────────────────

  getActiveTemplates(type?: TemplateType): Observable<ReceiptTemplate[]> {
    return this.templates$.pipe(
      map(templates => templates.filter(t =>
        t.isActive && (type == null || t.type === type),
      )),
    );
  }

  // ─── Export / Import ──────────────────────────────────────────────────────

  exportTemplate(template: ReceiptTemplate): void {
    const blob = new Blob([JSON.stringify(template, null, 2)], { type: 'application/json' });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement('a');
    a.href     = url;
    a.download = `${template.name.replace(/\s+/g, '_')}.json`;
    a.click();
    URL.revokeObjectURL(url);
  }

  importTemplate(json: string): Observable<ReceiptTemplate> {
    const source: ReceiptTemplate = JSON.parse(json);
    const imported = {
      ...source,
      id:              '',
      name:            `${source.name} (Imported)`,
      isGlobalDefault: false,
      isActive:        false,
    } as ReceiptTemplate;
    return this.saveTemplate(imported);
  }
}
